if (!customElements.get('lr-combo-upsell')) {
  customElements.define(
    'lr-combo-upsell',
    class LrComboUpsell extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;

        this.track = this.querySelector('[data-combo-track]');
        this.prevButton = this.querySelector('[data-combo-prev]');
        this.nextButton = this.querySelector('[data-combo-next]');
        this.errorElement = this.querySelector('[data-combo-error]');

        this.addEventListener('click', (event) => {
          const addButton = event.target.closest('[data-combo-add]');
          if (addButton) {
            event.preventDefault();
            this.swap(addButton);
            return;
          }
          if (event.target.closest('[data-combo-prev]')) this.step(-1);
          if (event.target.closest('[data-combo-next]')) this.step(1);
        });

        if (this.track && this.prevButton) {
          this.track.addEventListener('scroll', () => {
            if (this.scrollFrame) return;
            this.scrollFrame = requestAnimationFrame(() => {
              this.scrollFrame = null;
              this.updateArrows();
            });
          });
          this.updateArrows();
        }
      }

      step(direction) {
        const card = this.track.querySelector('.lr-combo-upsell__card');
        if (!card) return;
        const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.track.scrollBy({ left: direction * (card.offsetWidth + gap), behavior: reduceMotion ? 'auto' : 'smooth' });
      }

      updateArrows() {
        const maxScroll = this.track.scrollWidth - this.track.clientWidth;
        this.prevButton.disabled = this.track.scrollLeft <= 2;
        this.nextButton.disabled = this.track.scrollLeft >= maxScroll - 2;
      }

      getCartItems() {
        return (
          this.closest('cart-drawer-items') ||
          this.closest('cart-items') ||
          document.querySelector('cart-drawer-items') ||
          document.querySelector('cart-items')
        );
      }

      setBusy(activeButton, busy) {
        this.querySelectorAll('[data-combo-add]').forEach((button) => {
          button.disabled = busy;
        });
        activeButton.classList.toggle('loading', busy);
        const spinner = activeButton.querySelector('.loading__spinner');
        if (spinner) spinner.classList.toggle('hidden', !busy);
        this.setAttribute('aria-busy', busy ? 'true' : 'false');
      }

      showError(message, target = this) {
        const errorElement = target && target.querySelector('[data-combo-error]');
        if (errorElement) {
          errorElement.textContent = message;
          errorElement.hidden = false;
          return;
        }
        const cartErrors = document.getElementById('CartDrawer-CartErrors') || document.getElementById('cart-errors');
        if (cartErrors) cartErrors.textContent = message;
      }

      async postJSON(url, payload) {
        const response = await fetch(url, { ...fetchConfig('json'), body: JSON.stringify(payload) });
        const data = await response.json().catch(() => ({}));
        if (!response.ok || data.status) {
          const error = new Error(data.description || data.message || window.cartStrings?.error || 'Cart error');
          error.data = data;
          throw error;
        }
        return data;
      }

      async swap(button) {
        if (this.getAttribute('aria-busy') === 'true') return;

        const lineKey = this.dataset.lineKey;
        const variantId = Number(button.dataset.variantId);
        const comboTitle = button.dataset.comboTitle;
        const cartItems = this.getCartItems();
        const sections =
          cartItems && typeof cartItems.getSectionsToRender === 'function' ? cartItems.getSectionsToRender() : [];
        const blockId = this.id;

        if (this.errorElement) this.errorElement.hidden = true;
        this.setBusy(button, true);

        try {
          await this.postJSON(`${window.routes.cart_add_url}.js`, { items: [{ id: variantId, quantity: 1 }] });
        } catch (error) {
          this.setBusy(button, false);
          this.showError(error.message || 'We couldn’t add the 4-packs. Please try again.');
          return;
        }

        let parsedState;
        let swapError = '';
        try {
          const cart = await fetch(`${window.routes.cart_url}.js`, { headers: { Accept: 'application/json' } }).then(
            (response) => response.json()
          );
          const bottleLine = cart.items.find((item) => item.key === lineKey);
          if (bottleLine) {
            parsedState = await this.postJSON(`${window.routes.cart_change_url}.js`, {
              id: lineKey,
              quantity: Math.max(bottleLine.quantity - 1, 0),
              sections: sections.map((section) => section.section),
              sections_url: window.location.pathname,
            });
          }
        } catch (error) {
          swapError = `The 4-packs are in your cart, but we couldn’t remove the ${this.dataset.bottleName} bottle. Please lower its quantity below.`;
        }

        if (!parsedState) parsedState = await this.fetchSections(sections);
        if (parsedState) this.renderSections(sections, parsedState);

        const freshBlock = blockId ? document.getElementById(blockId) : null;
        if (swapError) {
          if (freshBlock && freshBlock !== this) {
            this.showError(swapError, freshBlock);
          } else {
            this.setBusy(button, false);
            this.showError(swapError);
          }
        } else if (!parsedState) {
          window.location.reload();
          return;
        }

        this.announce(swapError || `${comboTitle} added to your cart.`);
        this.restoreFocus(freshBlock);

        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState, variantId });
        }
      }

      async fetchSections(sections) {
        if (!sections.length) return null;
        try {
          const ids = sections.map((section) => section.section).join(',');
          const response = await fetch(`${window.location.pathname}?sections=${ids}`);
          const sectionData = await response.json();
          const cart = await fetch(`${window.routes.cart_url}.js`, { headers: { Accept: 'application/json' } }).then(
            (res) => res.json()
          );
          return { ...cart, sections: sectionData };
        } catch (error) {
          console.error(error);
          return null;
        }
      }

      renderSections(sections, parsedState) {
        if (!parsedState.sections) return;
        sections.forEach((section) => {
          const html = parsedState.sections[section.section];
          const host = document.getElementById(section.id);
          if (!html || !host) return;
          const target = host.querySelector(section.selector) || host;
          const source = new DOMParser().parseFromString(html, 'text/html').querySelector(section.selector);
          if (source) target.innerHTML = source.innerHTML;
        });

        const isEmpty = parsedState.item_count === 0;
        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer) cartDrawer.classList.toggle('is-empty', isEmpty);
        const cartFooter = document.getElementById('main-cart-footer');
        if (cartFooter) cartFooter.classList.toggle('is-empty', isEmpty);
      }

      announce(message) {
        const liveRegion =
          document.getElementById('CartDrawer-LiveRegionText') || document.getElementById('cart-live-region-text');
        if (!liveRegion) return;
        liveRegion.textContent = message;
        liveRegion.setAttribute('aria-hidden', 'false');
        setTimeout(() => liveRegion.setAttribute('aria-hidden', 'true'), 1000);
      }

      restoreFocus(freshBlock) {
        const cartDrawer = document.querySelector('cart-drawer');
        const inDrawer = cartDrawer && cartDrawer.classList.contains('active') && cartDrawer.querySelector('#CartDrawer');
        const focusTarget =
          (freshBlock && freshBlock.querySelector('[data-combo-track]')) ||
          (inDrawer ? cartDrawer.querySelector('.cart-item__name') : document.querySelector('.cart-item__name'));

        if (inDrawer && typeof trapFocus === 'function') {
          trapFocus(document.getElementById('CartDrawer'), focusTarget || cartDrawer.querySelector('.drawer__inner'));
        } else if (focusTarget) {
          focusTarget.focus();
        }
      }
    }
  );
}
